import React, { useState } from 'react';
import { BadgePercent, Info, X } from 'lucide-react';
import { formatPrice } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

const CouponCard = ({ coupon, productPrice = 0, onApply, isCompact = false }) => {
  const { t } = useTranslation();
  const [showTerms, setShowTerms] = useState(false);

  const discountText = coupon.type === 'percentage' 
    ? `${coupon.value}% OFF` 
    : `${formatPrice(coupon.value)} OFF`;

  const minPurchase = Number(coupon.minPurchaseAmount || 0);
  const isEligible = productPrice > 0 && productPrice >= minPurchase;
  const shortfall = minPurchase - productPrice;
  const cardHeight = isCompact ? 'h-[126px]' : 'h-[156px]';
  const code = coupon.code || coupon.id;
  const canApply = typeof onApply === 'function';
  const unlockLabel = !isEligible && productPrice > 0
    ? t('coupon.add_to_unlock', { amount: formatPrice(shortfall) }, `Add ${formatPrice(shortfall)} more`)
    : t('coupon.min_purchase', { amount: formatPrice(minPurchase) }, `Min. purchase: ${formatPrice(minPurchase)}`);

  return (
    <>
      <div className={`relative group ${cardHeight} overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_14px_34px_-28px_rgba(15,23,42,0.7)] ring-1 ring-white/70 transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_20px_42px_-30px_rgba(15,23,42,0.9)] dark:border-white/10 dark:bg-gray-900/90 dark:ring-white/5 dark:hover:border-white/20 ${isCompact ? 'p-3' : 'p-4'}`}>
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/70 to-transparent dark:via-sky-300/60" />
        <div className="relative z-10 flex h-full flex-col justify-between gap-2">
          <div className={`flex min-h-0 ${isCompact ? 'gap-2.5' : 'gap-3'} items-start`}>
            <div className={`${isCompact ? 'h-10 w-10' : 'h-12 w-12'} flex shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-100 dark:bg-sky-400/10 dark:text-sky-200 dark:ring-sky-400/20`}>
              <BadgePercent className={`${isCompact ? 'h-[18px] w-[18px]' : 'h-5 w-5'}`} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-1 flex min-w-0 items-center gap-1.5">
                <span className={`${isCompact ? 'text-base' : 'text-lg'} min-w-0 truncate font-black uppercase tracking-tight text-gray-950 dark:text-white notranslate`}>
                  {discountText}
                </span>
                <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-black uppercase text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-200 dark:ring-emerald-400/30">
                  Live
                </span>
              </div>

              <p className={`${isCompact ? 'text-[10px] line-clamp-2' : 'text-[11px] sm:text-xs line-clamp-2'} leading-relaxed text-gray-600 dark:text-gray-300`}>
                {coupon.description || `Use this code to get ${discountText} on eligible items.`}
              </p>
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); setShowTerms(true); }}
              className={`shrink-0 ${isCompact ? 'p-1.5' : 'p-2'} rounded-xl border border-gray-200/70 bg-gray-50 text-gray-500 transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-white/10 dark:bg-white/[0.08] dark:text-gray-300 dark:hover:border-sky-400/30 dark:hover:bg-sky-400/10 dark:hover:text-sky-200`}
              title="View Terms & Conditions"
            >
              <Info className={`${isCompact ? 'h-4 w-4' : 'h-5 w-5'}`} />
            </button>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1 rounded-xl border border-gray-200/70 bg-gray-50 px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.06]">
              <div className="text-[8px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Code
              </div>
              <div className="truncate text-xs font-black uppercase tracking-wide text-gray-950 dark:text-white notranslate">
                {code}
              </div>
            </div>

            {minPurchase > 0 && (
              <div className={`max-w-[118px] truncate rounded-xl px-2 py-2 text-[9px] font-black ${isEligible ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200' : 'bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-200'}`}>
                {unlockLabel}
              </div>
            )}

            {canApply && (
              <button
                onClick={(e) => { e.stopPropagation(); onApply(coupon); }}
                className="shrink-0 rounded-xl bg-gray-950 px-3 py-2 text-[10px] font-black text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
              >
                Apply
              </button>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showTerms && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={(e) => { e.stopPropagation(); setShowTerms(false); }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-auto"
              onClick={e => e.stopPropagation()}
            >
               <div className="flex justify-between items-start mb-6">
                 <div>
                   <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                     <Info className="text-primary-500 w-5 h-5"/> {t('coupon.terms_title', 'Coupon Terms')}
                   </h3>
                   <span className="text-xs font-bold text-primary-600 dark:text-primary-400 mt-1 uppercase tracking-widest">{coupon.code || coupon.id}</span>
                 </div>
                 <button onClick={(e) => { e.stopPropagation(); setShowTerms(false); }} className="p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-900 dark:hover:text-white rounded-full transition-colors">
                   <X className="w-4 h-4" />
                 </button>
               </div>
               
               <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
                 <div className="pb-3 border-b border-gray-100 dark:border-gray-800">
                   <p className="font-semibold text-gray-900 dark:text-white mb-1">Offer Description</p>
                   <p>{coupon.description || `Get ${discountText} your eligible order with this code.`}</p>
                 </div>
                 
                 <div className="grid grid-cols-2 gap-y-4 gap-x-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                   <div>
                     <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Discount Value</p>
                     <p className="font-medium text-gray-900 dark:text-white">{discountText}</p>
                   </div>
                   {coupon.maxDiscount > 0 && (
                     <div>
                       <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Max Discount</p>
                       <p className="font-medium text-gray-900 dark:text-white">{formatPrice(coupon.maxDiscount)}</p>
                     </div>
                   )}
                   <div>
                     <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Min. Purchase</p>
                     <p className="font-medium text-gray-900 dark:text-white">{minPurchase > 0 ? formatPrice(minPurchase) : 'None'}</p>
                   </div>
                   {coupon.endDate && (
                     <div>
                       <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Valid Until</p>
                       <p className="font-medium text-amber-600 dark:text-amber-500">{new Date(coupon.endDate).toLocaleDateString()}</p>
                     </div>
                   )}
                 </div>

                 {(coupon.allowedCategories?.length > 0 || coupon.allowedPeriods?.length > 0) && (
                   <div>
                     <p className="font-semibold text-gray-900 dark:text-white mb-2">Restrictions</p>
                     <ul className="list-disc pl-4 space-y-1 text-xs">
                       {coupon.allowedCategories?.length > 0 && (
                         <li>Valid only for categories: <span className="font-medium capitalize">{coupon.allowedCategories.join(', ')}</span></li>
                       )}
                       {coupon.allowedPeriods?.length > 0 && (
                         <li>Valid only for plans: <span className="font-medium capitalize">{coupon.allowedPeriods.join(', ')}</span></li>
                       )}
                     </ul>
                   </div>
                 )}
               </div>
               
               <button 
                onClick={(e) => { e.stopPropagation(); setShowTerms(false); }} 
                className="w-full mt-6 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-bold rounded-xl transition-colors active:scale-[0.98]"
               >
                 Close
               </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CouponCard;
